import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Utils } from 'app/components/utils/Utils';
import { FilterModel, OperatorFilterEnum } from 'app/models/filter-model';
import { UserModel } from 'app/models/user';
import { RestService } from 'app/services/rest/rest.service';
import { BehaviorSubject, Observable, catchError, debounceTime, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-user-search-select',
  templateUrl: './user-search-select.component.html',
  styleUrls: ['./user-search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSearchSelectComponent {

  @Input() label = 'finance.payment.assing.selectedUser';
  @Input() noDataLabel = 'finance.payment.assing.selectedUser.nodata';
  @Input() placeholder = 'Ej. Ana, ana@correo.com, +34...';
  @Input() minChars = 2;
  @Input() searchField = 'email';

  @Output() selectedUserChange = new EventEmitter<UserModel | null>();

  searchCtrl = new FormControl<string | UserModel>('');
  users$: Observable<UserModel[]>;
  loading$ = new BehaviorSubject<boolean>(false);
  searchTerm = '';

  private _selectedUser: UserModel | null = null;

  @Input()
  set selectedUser(value: UserModel | null) {
    this._selectedUser = value;
    if (!value) {
      this.searchCtrl.setValue('', { emitEvent: false });
    }
  }

  get selectedUser(): UserModel | null {
    return this._selectedUser;
  }

  constructor(private restService: RestService) {
    this.users$ = this.searchCtrl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((value) => {
        this.searchTerm = typeof value === 'string' ? value : value?.username ?? '';
        this.loading$.next(true);
      }),
      switchMap((value) => {
        const term = typeof value === 'string' ? value : value?.username ?? '';
        if (!term || term.trim().length < this.minChars) {
          this.loading$.next(false);
          return of([]);
        }

        const filters: FilterModel = {
          field: this.searchField,
          operator: OperatorFilterEnum.LIKE,
          value: term.trim()
        };

        return this.restService.searchUsers(Utils.generateFilters([filters])).pipe(
          map((result) => result.content ?? []),
          catchError(() => of([])),
          tap(() => this.loading$.next(false))
        );
      })
    );
  }

  displayUser = (user?: UserModel | null) => (user ? `${user.username} · ${user.email}` : '');

  onPick(user: UserModel, event: any) {
    if (!event?.isUserInput) {
      return;
    }

    this._selectedUser = user;
    this.selectedUserChange.emit(user);
  }

  clear() {
    this._selectedUser = null;
    this.searchTerm = '';
    this.searchCtrl.setValue('');
    this.selectedUserChange.emit(null);
  }
}
